import { useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import authService from '../services/authService';

/**
 * Custom hook to manage Auth0 token lifecycle
 * Handles token retrieval, storage, and refresh
 */
export const useAuth0Token = () => {
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();

  // Get and store the access token
  const getAndStoreToken = useCallback(async () => {
    if (!isAuthenticated) return null;

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          scope: 'openid profile email offline_access'
        }
      });
      
      // Store token in session storage
      sessionStorage.setItem('auth0Token', token);
      
      return token;
    } catch (error) {
      // Silently handle token errors for unauthenticated users
      if (isAuthenticated) {
        console.error('Failed to get access token:', error);
      }
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Refresh token when component mounts or auth state changes
  useEffect(() => {
    if (isAuthenticated) {
      getAndStoreToken();
    }
  }, [isAuthenticated, getAndStoreToken]);

  // Setup automatic token refresh before expiry
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh token every 50 minutes (tokens typically expire in 60 minutes)
    const intervalId = setInterval(async () => {
      try {
        await authService.refreshToken(getAccessTokenSilently);
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(intervalId);
  }, [isAuthenticated, getAccessTokenSilently]);

  // Initialize user on first authentication
  useEffect(() => {
    const initializeUser = async () => {
      if (isAuthenticated && user) {
        const token = await getAndStoreToken();
        if (token && !authService.getCurrentUser()) {
          try {
            await authService.loginWithAuth0(user, token);
          } catch (error) {
            console.error('Failed to initialize user:', error);
          }
        }
      }
    };

    initializeUser();
  }, [isAuthenticated, user, getAndStoreToken]);

  return {
    getAccessToken: getAndStoreToken,
    refreshToken: () => authService.refreshToken(getAccessTokenSilently)
  };
};

export default useAuth0Token;